import { ReactNode } from "react";
import {
    Type,
    Video,
    Link2,
    MousePointerClick,
    type LucideIcon,
} from "lucide-react";

import {
    type AccordionBlock,
    type AttachmentBlock,
    type AudioBlock,
    type EmbedBlock,
    type HtmlBlock,
    type CardSortingBlock,
    type ChecklistBlock,
    type ColumnsBlock,
    type EmbedProvider,
    type FlipCardsBlock,
    type KnowledgeCheckBlock,
    type TabsBlock,
    type LomdaV3Block,
    type LomdaV3BlockType,
    type MediaLayoutBlock,
    type StepsBlock,
    type TextBlock,
    type RatingBlock,
    type PollBlock,
    type WordCloudBlock,
    type OpenEndedBlock,
    type CompleteSentenceBlock,
    type ChartBlock,
    type SpacerBlock,
    type DividerBlock,
    type ButtonBlock,
} from "@/lib/utils/lomdaV3/types";
import { t } from "@/lib/localization";
import { useLomdaV3Editor } from "../store/useLomdaV3Editor";
import { lomdaLocaleOf } from "../hooks/useLomdaLocale";
import { BLOCK_AI_META, type BlockAIMeta } from "./blockAIMeta";
import type { BlockTag } from "./groupFormat";
import {
    createAccordionBlock,
    createAttachmentBlock,
    createAudioBlock,
    createCardSortingBlock,
    createChecklistBlock,
    createColumnsBlock,
    createEmbedBlock,
    createFlipCardsBlock,
    createStaticCardsBlock,
    STATIC_CARDS_LABEL_KEY,
    variantLabelKey,
    createHotspotBlock,
    createHowToBlock,
    createHtmlBlock,
    createKnowledgeCheckBlock,
    createMediaLayout,
    createScenarioBlock,
    createStepsBlock,
    createTabsBlock,
    createTextBlock,
    createWalkthroughBlock,
    createRatingBlock,
    createPollBlock,
    createWordCloudBlock,
    createOpenEndedBlock,
    createCompleteSentenceBlock,
    createChartBlock,
    createSpacerBlock,
    createDividerBlock,
    createButtonBlock,
} from "./blockRegistry";
import TextSettings from "../components/toolbar/textSettings";
import ColumnsSettings from "../components/toolbar/columnsSettings";
import CardSortingSettings from "../components/toolbar/cardSortingSettings";
import KnowledgeCheckSettings from "../components/toolbar/knowledgeCheckSettings";
import FlipCardsSettings from "../components/toolbar/flipCardsSettings";
import AccordionSettings from "../components/toolbar/accordionSettings";
import TabsSettings from "../components/toolbar/tabsSettings";
import ChecklistSettings from "../components/toolbar/checklistSettings";
import StepsSettings from "../components/toolbar/stepsSettings";
import RatingSettings from "../components/toolbar/ratingSettings";
import PollSettings from "../components/toolbar/pollSettings";
import WordCloudSettings from "../components/toolbar/wordCloudSettings";
import OpenEndedSettings from "../components/toolbar/openEndedSettings";
import CompleteSentenceSettings from "../components/toolbar/completeSentenceSettings";
import ChartSettings from "../components/toolbar/chartSettings";
import SpacerSettings from "../components/toolbar/spacerSettings";
import DividerSettings from "../components/toolbar/dividerSettings";
import ButtonSettings from "../components/toolbar/buttonSettings";
import { WidthControl } from "../components/toolbar/blockAppearance";
import {
    AudioSettings,
    AttachmentSettings,
} from "../components/toolbar/replaceMediaControl";
import { HtmlSettings } from "../components/toolbar/htmlSettings";
import { EmbedSettings } from "../components/toolbar/embedSettings";

/**
 * THE section registry — one entry per block type, the single place to configure
 * everything about a block: its name, thumbnail, whether it's `ready` (turn it
 * on/off), inline-edit behavior, its add-menu presets (`variants`), the
 * second-level topbar shown when it's focused, and its AI metadata.
 *
 * Derived from here (single source of truth): `BLOCK_CATEGORIES` (the add menu),
 * `BLOCK_TYPE_LABEL`, and `isInlineBlock`.
 *
 * NOTE: `secondLevelTopbar` holds React render functions, so this is a CLIENT
 * module. The AI generation workflow (server) must keep importing the pure
 * `blockAIMeta` module directly — never this file. The `ai` field here is a
 * client-side convenience copy of the same metadata.
 */

export type BlockCategoryKey =
    | "text"
    | "media"
    | "embed"
    | "interactive";

/** Add-menu category order + presentation (icon + label). */
const CATEGORY_ORDER: BlockCategoryKey[] = [
    "text",
    "media",
    "embed",
    "interactive",
];

const CATEGORY_META: Record<
    BlockCategoryKey,
    { icon: LucideIcon; labelKey: string }
> = {
    text: { icon: Type, labelKey: "lomda-v3-editor.category.text" },
    media: { icon: Video, labelKey: "lomda-v3-editor.category.media" },
    embed: { icon: Link2, labelKey: "lomda-v3-editor.category.embed" },
    interactive: {
        icon: MousePointerClick,
        labelKey: "lomda-v3-editor.category.interactive",
    },
};

/** One add-menu preset for a section. Carries its own category so a single
 * block type (e.g. media-layout) can surface presets under several categories. */
export interface SectionVariant {
    key: string;
    labelKey: string;
    /** Block-library card title (Figma design). Falls back to labelKey. */
    titleKey?: string;
    /** Block-library card description (Figma design). */
    descriptionKey?: string;
    category: BlockCategoryKey;
    create: () => LomdaV3Block;
    /** Per-variant on/off (defaults on). The whole section's `ready` wins. */
    ready?: boolean;
}

export interface SectionDef {
    type: LomdaV3BlockType;
    /** i18n key for the block's display name. */
    nameKey: string;
    /** Optional thumbnail image URL (none yet — the add menu uses procedural
     * thumbnails). Reserved so a real asset can be dropped in per section. */
    thumbnail: string | null;
    /** Turn the whole block on/off everywhere (add menu, etc.). */
    ready: boolean;
    /** Temporarily disabled: hidden from the add menu AND not generated by the
     *  AI, but the code stays in the app. Flip to `false`/remove to re-enable. */
    inactive?: boolean;
    /** Inline-edit on canvas vs edit in a modal. */
    isInline: boolean;
    /** The topbar shown when a block of this type is focused (step 3 of the
     * renderer). Native rich-text/media topbars are NOT here — see the renderer. */
    secondLevelTopbar?: (block: LomdaV3Block) => ReactNode;
    /** AI metadata (client copy of blockAIMeta; server imports blockAIMeta). */
    ai: BlockAIMeta;
    /** Add-menu presets (≥1). */
    variants: SectionVariant[];
    /** Default tag of the block's rich-text fields: the empty seed, and what a
     *  Ctrl+A replace leaves. Unlisted fields default to Paragraph. */
    fieldTags?: Partial<Record<TextField, BlockTag>>;
}

export type TextField =
    "question" | "title" | "body" | "category" | "quote" | "author";


// Stored in the lomda's language, whatever language the editor UI is in.
const createDefaultButton = () =>
    createButtonBlock(
        t(
            "lomda-v3-editor.button.default-label",
            lomdaLocaleOf(useLomdaV3Editor.getState().metadata.language)
        )
    );

const embedVariant = (provider: EmbedProvider): SectionVariant => ({
    key: provider,
    labelKey: `lomda-v3-editor.embed-variant.${provider}`,
    category: "embed",
    create: () => createEmbedBlock(provider),
});

const renderCardSortingTopbar = (b: LomdaV3Block): ReactNode => (
    <CardSortingSettings block={b as CardSortingBlock} />
);

/** Width-only topbar for simple blocks (audio/attachment/embed/html) that have
 *  no other settings — keeps "width on every block" consistent. */
const renderWidthTopbar = (b: LomdaV3Block): ReactNode => (
    <WidthControl blockId={b.id} />
);

/** The second-level topbar a block gets by default (unless it sets its own).
 * Count steppers live only on multi-columns/columns/table (via their own
 * settings) + card-sorting; list blocks manage items in their modal editor. */
function defaultTopbar(
    type: LomdaV3BlockType
): SectionDef["secondLevelTopbar"] {
    if (type === "card-sorting") return renderCardSortingTopbar;
    return undefined;
}

const section = (
    def: Omit<SectionDef, "ai"> & { ai?: BlockAIMeta }
): SectionDef => ({
    ...def,
    ai: def.ai ?? BLOCK_AI_META[def.type],
    secondLevelTopbar: def.secondLevelTopbar ?? defaultTopbar(def.type),
});

export const SECTION_REGISTRY: SectionDef[] = [
    section({
        type: "text",
        fieldTags: { quote: "h2", author: "h6" },
        nameKey: "lomda-v3-editor.block-type.text",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <TextSettings block={b as TextBlock} />,
        variants: [
            { key: "text", labelKey: "lomda-v3-editor.text-variant.text", titleKey: "lomda-v3-editor.block-card.text.title", descriptionKey: "lomda-v3-editor.block-card.text.description", category: "text", create: () => createTextBlock("text") },
            { key: "quote", labelKey: "lomda-v3-editor.text-variant.quote", titleKey: "lomda-v3-editor.block-card.quote.title", descriptionKey: "lomda-v3-editor.block-card.quote.description", category: "text", create: () => createTextBlock("quote") },
            { key: "table", labelKey: "lomda-v3-editor.text-variant.table", titleKey: "lomda-v3-editor.block-card.table.title", descriptionKey: "lomda-v3-editor.block-card.table.description", category: "text", create: () => createTextBlock("table") },
        ],
    }),
    section({
        type: "media-layout",
        nameKey: "lomda-v3-editor.block-type.media",
        thumbnail: null,
        ready: true,
        isInline: true,
        // Media is a "native" topbar handled directly by the renderer (step 2),
        // so no registry topbar here.
        // A single media section. For text above/below, the user adds a separate
        // rich-text block (no top/bottom caption variants).
        variants: [
            { key: "image", labelKey: "lomda-v3-editor.media-variant.image", titleKey: "lomda-v3-editor.block-card.image.title", descriptionKey: "lomda-v3-editor.block-card.image.description", category: "media", create: () => createMediaLayout("image", "none", 1) },
            { key: "video", labelKey: "lomda-v3-editor.media-variant.video", titleKey: "lomda-v3-editor.block-card.video.title", descriptionKey: "lomda-v3-editor.block-card.video.description", category: "media", create: () => createMediaLayout("video", "none", 1) },
        ],
    }),
    section({
        type: "audio",
        nameKey: "lomda-v3-editor.block-type.audio",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <AudioSettings block={b as AudioBlock} />,
        variants: [
            { key: "single-audio", labelKey: "lomda-v3-editor.media-variant.single-audio", titleKey: "lomda-v3-editor.block-card.audio.title", descriptionKey: "lomda-v3-editor.block-card.audio.description", category: "media", create: createAudioBlock },
        ],
    }),
    section({
        type: "attachment",
        // Same label the block-picker thumbnail uses, so the topbar and the
        // picker can't say different things ("Document", not "Attachment").
        nameKey: "lomda-v3-editor.media-variant.document",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => (
            <AttachmentSettings block={b as AttachmentBlock} />
        ),
        variants: [
            { key: "document", labelKey: "lomda-v3-editor.media-variant.document", titleKey: "lomda-v3-editor.block-card.document.title", descriptionKey: "lomda-v3-editor.block-card.document.description", category: "media", create: createAttachmentBlock },
        ],
    }),
    section({
        type: "embed",
        nameKey: "lomda-v3-editor.block-type.embed",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <EmbedSettings block={b as EmbedBlock} />,
        variants: [
            embedVariant("web-link"),
            embedVariant("canva"),
            embedVariant("microsoft"),
            embedVariant("google"),
            embedVariant("surveymonkey"),
            embedVariant("vimeo"),
            embedVariant("slideshare"),
            embedVariant("prezi"),
            embedVariant("youtube"),
            embedVariant("powtoon"),
            embedVariant("h5p"),
        ],
    }),
    section({
        type: "html",
        nameKey: "lomda-v3-editor.block-type.html",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <HtmlSettings block={b as HtmlBlock} />,
        variants: [
            { key: "html", labelKey: "lomda-v3-editor.embed-variant.html", category: "embed", create: createHtmlBlock },
        ],
    }),
    section({
        type: "walkthrough",
        nameKey: "lomda-v3-editor.block-type.walkthrough",
        thumbnail: null,
        ready: true,
        inactive: true,
        isInline: false,
        variants: [
            { key: "walkthrough", labelKey: "lomda-v3-editor.interactive.walkthrough", descriptionKey: "lomda-v3-editor.block-card.walkthrough.description", category: "interactive", create: createWalkthroughBlock },
        ],
    }),
    section({
        type: "scenario",
        nameKey: "lomda-v3-editor.interactive.scenario",
        thumbnail: null,
        ready: true,
        inactive: true,
        isInline: false,
        variants: [
            { key: "scenario", labelKey: "lomda-v3-editor.interactive.scenario", descriptionKey: "lomda-v3-editor.block-card.scenario.description", category: "interactive", create: createScenarioBlock },
        ],
    }),
    section({
        type: "checklist",
        nameKey: "lomda-v3-editor.interactive.checklist",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => (
            <ChecklistSettings block={b as ChecklistBlock} />
        ),
        variants: [
            { key: "checklist", labelKey: "lomda-v3-editor.interactive.checklist", descriptionKey: "lomda-v3-editor.block-card.checklist.description", category: "interactive", create: createChecklistBlock },
        ],
    }),
    section({
        type: "card-sorting",
        fieldTags: { category: "h3" },
        nameKey: "lomda-v3-editor.interactive.card-sorting",
        thumbnail: null,
        ready: true,
        isInline: true,
        variants: [
            { key: "card-sorting", labelKey: "lomda-v3-editor.interactive.card-sorting", descriptionKey: "lomda-v3-editor.block-card.card-sorting.description", category: "interactive", create: createCardSortingBlock },
        ],
    }),
    section({
        type: "how-to",
        nameKey: "lomda-v3-editor.interactive.how-to",
        thumbnail: null,
        ready: true,
        inactive: true,
        isInline: false,
        variants: [
            { key: "how-to", labelKey: "lomda-v3-editor.interactive.how-to", descriptionKey: "lomda-v3-editor.block-card.how-to.description", category: "interactive", create: createHowToBlock },
        ],
    }),
    section({
        type: "hotspot",
        nameKey: "lomda-v3-editor.interactive.hotspot",
        thumbnail: null,
        ready: true,
        inactive: true,
        isInline: false,
        variants: [
            { key: "hotspot", labelKey: "lomda-v3-editor.interactive.hotspot", titleKey: "lomda-v3-editor.block-card.hotspot.title", descriptionKey: "lomda-v3-editor.block-card.hotspot.description", category: "interactive", create: createHotspotBlock },
        ],
    }),
    section({
        type: "flip-cards",
        nameKey: "lomda-v3-editor.interactive.flip-cards",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => (
            <FlipCardsSettings block={b as FlipCardsBlock} />
        ),
        variants: [
            { key: "flip-cards", labelKey: "lomda-v3-editor.interactive.flip-cards", titleKey: "lomda-v3-editor.block-card.flip-cards.title", descriptionKey: "lomda-v3-editor.block-card.flip-cards.description", category: "interactive", create: createFlipCardsBlock },
            { key: "static-cards", labelKey: STATIC_CARDS_LABEL_KEY, titleKey: "lomda-v3-editor.block-card.static-cards.title", descriptionKey: "lomda-v3-editor.block-card.static-cards.description", category: "interactive", create: createStaticCardsBlock },
        ],
    }),
    section({
        type: "knowledge-check",
        fieldTags: { question: "h5" },
        nameKey: "lomda-v3-editor.interactive.knowledge-check",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => (
            <KnowledgeCheckSettings block={b as KnowledgeCheckBlock} />
        ),
        variants: [
            { key: "knowledge-check", labelKey: "lomda-v3-editor.interactive.knowledge-check", titleKey: "lomda-v3-editor.block-card.knowledge-check.title", descriptionKey: "lomda-v3-editor.block-card.knowledge-check.description", category: "interactive", create: createKnowledgeCheckBlock },
        ],
    }),
    section({
        type: "steps",
        fieldTags: { title: "h5" },
        nameKey: "lomda-v3-editor.interactive.steps",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <StepsSettings block={b as StepsBlock} />,
        variants: [
            { key: "steps", labelKey: "lomda-v3-editor.interactive.steps", descriptionKey: "lomda-v3-editor.block-card.steps.description", category: "interactive", create: createStepsBlock },
        ],
    }),
    section({
        type: "tabs",
        nameKey: "lomda-v3-editor.interactive.tabs",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <TabsSettings block={b as TabsBlock} />,
        variants: [
            { key: "tabs", labelKey: "lomda-v3-editor.interactive.tabs", titleKey: "lomda-v3-editor.block-card.tabs.title", descriptionKey: "lomda-v3-editor.block-card.tabs.description", category: "interactive", create: createTabsBlock },
        ],
    }),
    section({
        type: "accordion",
        fieldTags: { title: "h5", body: "h6" },
        nameKey: "lomda-v3-editor.interactive.accordion",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => (
            <AccordionSettings block={b as AccordionBlock} />
        ),
        variants: [
            { key: "accordion", labelKey: "lomda-v3-editor.interactive.accordion", titleKey: "lomda-v3-editor.block-card.accordion.title", descriptionKey: "lomda-v3-editor.block-card.accordion.description", category: "interactive", create: createAccordionBlock },
        ],
    }),
    section({
        type: "rating",
        fieldTags: { question: "h5" },
        nameKey: "lomda-v3-editor.interactive.rating",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <RatingSettings block={b as RatingBlock} />,
        variants: [
            { key: "rating", labelKey: "lomda-v3-editor.interactive.rating", titleKey: "lomda-v3-editor.block-card.rating.title", descriptionKey: "lomda-v3-editor.block-card.rating.description", category: "interactive", create: createRatingBlock },
        ],
    }),
    section({
        type: "poll",
        fieldTags: { question: "h5" },
        nameKey: "lomda-v3-editor.interactive.poll",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <PollSettings block={b as PollBlock} />,
        variants: [
            { key: "poll", labelKey: "lomda-v3-editor.interactive.poll", titleKey: "lomda-v3-editor.block-card.poll.title", descriptionKey: "lomda-v3-editor.block-card.poll.description", category: "interactive", create: createPollBlock },
        ],
    }),
    section({
        type: "word-cloud",
        fieldTags: { question: "h5" },
        nameKey: "lomda-v3-editor.interactive.word-cloud",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <WordCloudSettings block={b as WordCloudBlock} />,
        variants: [
            { key: "word-cloud", labelKey: "lomda-v3-editor.interactive.word-cloud", titleKey: "lomda-v3-editor.block-card.word-cloud.title", descriptionKey: "lomda-v3-editor.block-card.word-cloud.description", category: "interactive", create: createWordCloudBlock },
        ],
    }),
    section({
        type: "open-ended",
        fieldTags: { question: "h5" },
        nameKey: "lomda-v3-editor.interactive.open-ended",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <OpenEndedSettings block={b as OpenEndedBlock} />,
        variants: [
            { key: "open-ended", labelKey: "lomda-v3-editor.interactive.open-ended", titleKey: "lomda-v3-editor.block-card.open-ended.title", descriptionKey: "lomda-v3-editor.block-card.open-ended.description", category: "interactive", create: createOpenEndedBlock },
        ],
    }),
    section({
        type: "complete-sentence",
        nameKey: "lomda-v3-editor.interactive.complete-sentence",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <CompleteSentenceSettings block={b as CompleteSentenceBlock} />,
        variants: [
            { key: "complete-sentence", labelKey: "lomda-v3-editor.interactive.complete-sentence", titleKey: "lomda-v3-editor.block-card.complete-sentence.title", descriptionKey: "lomda-v3-editor.block-card.complete-sentence.description", category: "interactive", create: createCompleteSentenceBlock },
        ],
    }),
    section({
        type: "chart",
        fieldTags: { title: "h5" },
        nameKey: "lomda-v3-editor.interactive.chart",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <ChartSettings block={b as ChartBlock} />,
        variants: [
            { key: "chart", labelKey: "lomda-v3-editor.interactive.chart", titleKey: "lomda-v3-editor.block-card.chart.title", descriptionKey: "lomda-v3-editor.block-card.chart.description", category: "interactive", create: createChartBlock },
        ],
    }),
    section({
        type: "columns",
        nameKey: "lomda-v3-editor.layout.columns",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <ColumnsSettings block={b as ColumnsBlock} />,
        variants: [
            { key: "columns", labelKey: "lomda-v3-editor.layout.columns", titleKey: "lomda-v3-editor.block-card.multi-column.title", descriptionKey: "lomda-v3-editor.block-card.multi-column.description", category: "text", create: createColumnsBlock },
        ],
    }),
    section({
        type: "spacer",
        nameKey: "lomda-v3-editor.layout.spacer",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <SpacerSettings block={b as SpacerBlock} />,
        variants: [
            { key: "spacer", labelKey: "lomda-v3-editor.layout.spacer", descriptionKey: "lomda-v3-editor.block-card.spacer.description", category: "text", create: createSpacerBlock },
        ],
    }),
    section({
        type: "divider",
        nameKey: "lomda-v3-editor.layout.divider",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <DividerSettings block={b as DividerBlock} />,
        variants: [
            { key: "divider", labelKey: "lomda-v3-editor.layout.divider", descriptionKey: "lomda-v3-editor.block-card.divider.description", category: "text", create: createDividerBlock },
        ],
    }),
    section({
        type: "button",
        nameKey: "lomda-v3-editor.layout.button",
        thumbnail: null,
        ready: true,
        isInline: true,
        secondLevelTopbar: (b) => <ButtonSettings block={b as ButtonBlock} />,
        variants: [
            { key: "button", labelKey: "lomda-v3-editor.layout.button", descriptionKey: "lomda-v3-editor.block-card.button.description", category: "text", create: createDefaultButton },
        ],
    }),
];

export const SECTION_BY_TYPE: Record<LomdaV3BlockType, SectionDef> =
    Object.fromEntries(SECTION_REGISTRY.map((s) => [s.type, s])) as Record<
        LomdaV3BlockType,
        SectionDef
    >;

// ── Derived, back-compat exports (single source = SECTION_REGISTRY) ──

/** A palette preset plus the block type it creates. */
export type BlockVariant = SectionVariant & { type: LomdaV3BlockType };

export interface BlockCategory {
    key: BlockCategoryKey;
    icon: LucideIcon;
    labelKey: string;
    variants: BlockVariant[];
}

/** The add menu, grouped by category. Only `ready` sections/variants appear. */
export const BLOCK_CATEGORIES: BlockCategory[] = CATEGORY_ORDER.map((key) => ({
    key,
    icon: CATEGORY_META[key].icon,
    labelKey: CATEGORY_META[key].labelKey,
    variants: SECTION_REGISTRY.filter((s) => s.ready && !s.inactive).flatMap(
        (s) =>
            s.variants
                .filter((v) => v.category === key && v.ready !== false)
                .map((v) => ({ ...v, type: s.type }))
    ),
})).filter((cat) => cat.variants.length > 0);

export const BLOCK_TYPE_LABEL: Record<LomdaV3BlockType, string> =
    Object.fromEntries(
        SECTION_REGISTRY.map((s) => [s.type, s.nameKey])
    ) as Record<LomdaV3BlockType, string>;

export const labelKeyOf = (
    type: LomdaV3BlockType,
    variantKey: string | undefined
): string => variantKey ?? BLOCK_TYPE_LABEL[type];

export const blockLabelKey = (block: LomdaV3Block): string =>
    labelKeyOf(block.type, variantLabelKey(block));

export const isInlineBlock = (type: LomdaV3BlockType): boolean =>
    SECTION_BY_TYPE[type]?.isInline ?? false;

export const fieldTag = (type: LomdaV3BlockType, field: TextField): BlockTag =>
    SECTION_BY_TYPE[type]?.fieldTags?.[field] ?? "paragraph";
